import { baseURL } from "@/config/APIConfig";
import {
  START_LOADING,
  SET_USER_DETAIL,
  UPDATE_PASSWORD,
  SET_USER_TOKEN,
} from "./constant";
import { APICall } from "@/config/NetworkUtility";
import { setAuthToken } from "@/utility/CommonConstant";
import axios from "axios";

import { sessionLogin, sessionRecordFailed } from "@/services/sessionClient";

/**
 * Get app data
 */
export const loadAppData = (isFirstTime = false) => {
  return (dispatch) => {
    return Promise.all([dispatch(userDataAction())])
      .then((res) => {
        return Promise.resolve(res);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  };
};

/* ==========================================
   COMMON AUTH TOKEN
========================================== */

export const getAuthToken = async () => {
  const payloadToken = JSON.stringify({
    username: "devauth",
    password: "devauth@987",
  });

  const result = await APICall("post", payloadToken, "Auth/GetToken");

  if (result?.statuscode === 200 && result?.token) {
    return {
      token: result.token,
      response: result,
    };
  }

  throw new Error("Authentication failed");
};

/**
 * Extend the current session in place.
 *
 * The API token is issued to the application rather than to the user's
 * password, so it can be reissued without a sign-in. The stored user details
 * are kept as they are — only the credential and its expiry move forward.
 */
export const renewAuthToken = async () => {
  const { token } = await getAuthToken();

  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  } catch {
    /* unreadable — setAuthToken still writes a usable shape */
  }

  setAuthToken(token, "", "", storedUser);
  return token;
};

/**
 * Login Action - Uses production API via backend proxy
 * All calls go through /api/proxy/ to api.shipez.ai
 * @param {*} payload
 * @returns
 */
export const getLoginToken = (payload) => {
  return async (dispatch) => {
    // First get auth token from devauth via proxy
    const payloadToken = JSON.stringify({
      username: "devauth",
      password: "devauth@987",
    });

    return APICall("post", payloadToken, "Auth/GetToken").then(
      async (result) => {
        if (result.statuscode === 200) {
          const authToken = result?.token;
          if (authToken) {
            const udetails = await dispatch(getUserDetails(payload, authToken));
            if (udetails) {
              dispatch({
                type: SET_USER_TOKEN,
                payload: result ?? null,
              });
              return Promise.resolve(authToken);
            } else {
              return Promise.reject("User not found");
            }
          }
        }
        return Promise.reject("Authentication failed");
      },
      (error) => {
        console.error("Login error:", error);
        return Promise.reject(error);
      },
    );
  };
};
/* ==========================================
   SUPER ADMIN LOGIN
========================================== */

export const getSuperAdminToken = (payload) => {
  return async (dispatch) => {
    try {
      const { token, response } = await getAuthToken();

      const userDetails = await dispatch(getSuperAdminDetails(payload, token));

      if (!userDetails) {
        return Promise.reject("Super Admin not found");
      }

      dispatch({
        type: SET_USER_TOKEN,
        payload: response,
      });

      return Promise.resolve(token);
    } catch (error) {
      console.error("Super Admin login error:", error);
      return Promise.reject(error);
    }
  };
};

/**
 * Get UserData Login - via proxy
 */
export const getUserDetails = (payload, token) => {
  return (dispatch) => {
    return APICall("post", payload, "user/Login", {
      headers: { Authorization: `Bearer ${token}` },
    }).then(
      (result) => {
        // console.log("User details result:", result);
        if (result?.statuscode === 200) {
          // Register session in local backend for single-session enforcement
          const userData = Array.isArray(result.data) ? result.data[0] : result.data;
          return sessionLogin({ user: userData || {} }).then((sess) => {
            if (sess && sess.conflict) {
              // Stash user + token so the Login page can retry with force=true
              try {
                localStorage.setItem("shipez.pendingUser", JSON.stringify(userData || {}));
                localStorage.setItem("shipez.pendingToken", token || "");
                localStorage.setItem("shipez.pendingResult", JSON.stringify(result?.data || {}));
              } catch (_) { /* ignore */ }
              const err = new Error(sess.message || "User already signed in on another device");
              err.sessionConflict = sess;
              return Promise.reject(err);
            }
            setAuthToken(token, "", "", result?.data);
            dispatch({
              type: SET_USER_DETAIL,
              payload: result?.data || {},
            });
            return result?.data;
          }).catch((e) => {
            if (e && e.sessionConflict) return Promise.reject(e);
            console.warn("Session tracker unavailable:", e?.message);
            setAuthToken(token, "", "", result?.data);
            dispatch({
              type: SET_USER_DETAIL,
              payload: result?.data || {},
            });
            return result?.data;
          });
        } else {
          sessionRecordFailed({ email: JSON.parse(payload)?.username, reason: result?.message || "invalid credentials" });
          return Promise.reject(result?.message || "Login failed");
        }
      },
      (error) => {
        console.error("getUserDetails error:", error);
        return Promise.reject(error);
      },
    );
  };
};

/* ==========================================
   SUPER ADMIN DETAILS
========================================== */

export const getSuperAdminDetails = (payload, token) => {
  return (dispatch) => {
    return APICall("post", payload, "user/SuperLogin", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(
      (result) => {
        if (result?.statuscode === 200) {
          setAuthToken(token, "", "", result?.data);

          const userData = Array.isArray(result.data)
            ? result.data[0]
            : result.data;

          dispatch({
            type: SET_USER_DETAIL,
            payload: {
              ...userData,
              isSuperAdmin: true,
            },
          });

          return result.data;
        }

        return Promise.reject(result?.message || "Super Admin login failed");
      },
      (error) => {
        console.error("getSuperAdminDetails error:", error);

        return Promise.reject(error);
      },
    );
  };
};

/**
 * User data action placeholder
 */
export const userDataAction = () => {
  return (dispatch) => {
    return Promise.resolve();
  };
};
